import {EventEmitter, signal} from "@angular/core"
import {Vector2Like} from "@cm/math"
import {deepCopy} from "@cm/utils"
import {OperatorPanelComponentType, OperatorProcessingHints} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {OperatorBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-base"
import {OperatorCallback} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-callback"
import {ShiftPanelComponent} from "@app/textures/texture-editor/operator-stack/operators/shift/panel/shift-panel.component"
import {ShiftToolbox} from "@app/textures/texture-editor/operator-stack/operators/shift/toolbox/shift-toolbox"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {ImageOpNodeOperatorShift} from "@app/textures/texture-editor/operator-stack/operators/shift/node/node"
import {AnyImageOpResultNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"

export class OperatorShift extends OperatorBase<TextureEditNodes.OperatorShift> {
    readonly shiftInPixelsChanged = new EventEmitter<Vector2Like>()
    readonly showGuidesChanged = new EventEmitter<boolean>()

    readonly panelComponentType: OperatorPanelComponentType = ShiftPanelComponent
    readonly canvasToolbox: ShiftToolbox

    readonly type = "operator-shift" as const

    constructor(callback: OperatorCallback, node: TextureEditNodes.OperatorShift | null) {
        super(
            callback,
            deepCopy(node) ?? {
                type: "operator-shift",
                enabled: true,
                shiftInPixels: {
                    x: 0,
                    y: 0,
                },
            },
        )

        this.canvasToolbox = new ShiftToolbox(this)
    }

    // OperatorBase
    override dispose(): void {
        super.dispose()
        this.canvasToolbox.remove()
    }

    // OperatorBase
    async clone() {
        const clonedOperator = new OperatorShift(this.callback, deepCopy(this.node))
        await clonedOperator.init()
        return clonedOperator
    }

    // OperatorBase
    override async createImageOpGraph(sourceImage: AnyImageOpResultNode, hints: OperatorProcessingHints, imageOpNodeBag: ImageOpNodeBag) {
        return imageOpNodeBag.getNode("operatorShift", ImageOpNodeOperatorShift, {
            sourceImage,
            shiftInPixels: this.node.shiftInPixels,
        })
    }

    get shiftInPixels(): Vector2Like {
        return this.node.shiftInPixels
    }

    set shiftInPixels(value: Vector2Like) {
        if (this.node.shiftInPixels.x === value.x && this.node.shiftInPixels.y === value.y) {
            return
        }
        this.node.shiftInPixels = {x: value.x, y: value.y}
        this.markEdited()
        this.requestEval()
        this.shiftInPixelsChanged.emit(value)
    }

    private readonly _$showGuides = signal(true)
    readonly $showGuides = this._$showGuides.asReadonly()

    setShowGuides(value: boolean): void {
        if (this.$showGuides() === value) {
            return
        }
        this._$showGuides.set(value)
        this.showGuidesChanged.emit(value)
    }
}
